import { Link } from '@tanstack/react-router';
import { ArrowRight, ChartLine, Send, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useStore } from '@/shared/store/store-context';

import { NetworkStatusBadge } from '../components/network-status-badge';
import { NetworkTile } from '../components/network-tile';
import { useSocialConnections } from '../hooks/use-social-connections';
import { SOCIAL_NETWORKS, type SocialNetworkId } from '../networks';
import { accountHandle, connectionState, findConnection } from '../schemas/channel-state';

function PinterestHubCard({ storeId }: { storeId: string }) {
  const { t } = useTranslation('social');
  const format = useFormat();
  const connections = useSocialConnections(storeId);
  const name = t('pinterest.name');
  const connection = connections.data
    ? findConnection(connections.data.items, 'pinterest')
    : undefined;
  const state = connectionState(connection);
  const params = { storeId };

  const account =
    connection && state !== 'disconnected'
      ? t('hub.account', {
          account: accountHandle(connection),
          when: format.relative(connection.connectedAt),
        })
      : t('hub.notConnected');

  return (
    <Card role="region" aria-label={name} className="gap-4">
      <CardHeader className="flex flex-row items-start gap-3">
        <NetworkTile network="pinterest" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <CardTitle className="flex flex-wrap items-center gap-2 text-[15px]">
            {name}
            {connections.isPending ? null : <NetworkStatusBadge state={state} />}
          </CardTitle>
          {connections.isPending ? (
            <Skeleton className="h-4 w-40" />
          ) : (
            <CardDescription className="truncate">{account}</CardDescription>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {connections.error ? (
          <ErrorState error={connections.error} onRetry={() => void connections.refetch()} />
        ) : (
          <p className="text-muted-foreground text-sm">{t('pinterest.hint')}</p>
        )}
        {state === 'connected' ? (
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/s/$storeId/social/pinterest/posts" params={params}>
                <Send aria-hidden />
                {t('nav.posts')}
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/s/$storeId/social/pinterest/analytics" params={params}>
                <ChartLine aria-hidden />
                {t('nav.analytics')}
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/s/$storeId/social/pinterest/settings" params={params}>
                <Settings aria-hidden />
                {t('nav.settings')}
              </Link>
            </Button>
          </div>
        ) : null}
      </CardContent>
      <CardFooter className="mt-auto">
        <Button asChild>
          {state === 'connected' ? (
            <Link to="/s/$storeId/social/pinterest" params={params}>
              {t('hub.open', { network: name })}
              <ArrowRight aria-hidden />
            </Link>
          ) : (
            <Link to="/s/$storeId/social/pinterest/connection" params={params}>
              {state === 'disconnected'
                ? t('hub.setUp', { network: name })
                : t('hub.fix', { network: name })}
              <ArrowRight aria-hidden />
            </Link>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}

function ComingSoonCard({ network }: { network: SocialNetworkId }) {
  const { t } = useTranslation('social');
  const name = t(`${network}.name`);
  return (
    <Card role="region" aria-label={name} className="bg-muted/30 gap-4 border-dashed">
      <CardHeader className="flex flex-row items-start gap-3">
        <NetworkTile network={network} className="opacity-70" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <CardTitle className="flex flex-wrap items-center gap-2 text-[15px]">
            {name}
            <Badge variant="outline" className="border-border text-muted-foreground">
              {t('hub.comingSoon')}
            </Badge>
          </CardTitle>
          <CardDescription>{t(`${network}.hint`)}</CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
}

function Hub() {
  const { t } = useTranslation('social');
  const store = useStore();
  return (
    <PageContent>
      <PageHeader
        title={t('hub.title')}
        description={t('hub.description', { store: store.name })}
      />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-tour="social.hub.networks">
        {SOCIAL_NETWORKS.map((network) =>
          network.id === 'pinterest' ? (
            <PinterestHubCard key={network.id} storeId={store.id} />
          ) : (
            <ComingSoonCard key={network.id} network={network.id} />
          ),
        )}
      </div>
    </PageContent>
  );
}

/** Marketing › Social media: one card per network; a live network opens its own section. */
export function SocialHubPage() {
  return (
    <FeatureErrorBoundary name="social.hub">
      <Hub />
    </FeatureErrorBoundary>
  );
}
